import type { MessageResponse } from '@/domain/Api';
import { api } from '@/lib/api';
import { routes } from '@/routes/routes';
import { mutationOptions, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import type { CreateAddressRequest } from '../addresses/addressMutations';
import { queryKeys } from '../queryKeys';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface SignupRequest extends LoginRequest {
  name: string;
  surname: string;
  phone: string;
}

export interface RegisterRequest extends SignupRequest {
  address?: CreateAddressRequest;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string | undefined;
  email: string | undefined;
  password: string;
}

export interface UserResponse {
  data: {
    access_token: string;
    refresh_token: string;
  };
}

export interface LoginResponse {
  data: {
    access_token: string;
    refresh_token: string;
  };
}

export const loginMutation = () => {
  return mutationOptions({
    mutationFn: async (values: LoginRequest) => {
      return await api
        .post('auth/login', { json: values })
        .json<LoginResponse>();
    },
  });
};

export const signupMutation = mutationOptions({
  mutationFn: async (values: SignupRequest) =>
    api.post('auth/register', { json: values }).json<UserResponse>(),
});

export const forgotPasswordMutation = mutationOptions({
  mutationFn: async (values: ForgotPasswordRequest) =>
    api.post('auth/forgot-password', { json: values }).json<MessageResponse>(),
  onSuccess: () => {
    toast.success('Password reset link sent! Please check your email.');
  },
  onError: (error) => {
    toast.error(error.message || 'Failed to send reset link');
  },
});

export const resetPasswordMutation = () => {
  const navigate = useNavigate();

  return mutationOptions({
    mutationFn: async (values: ResetPasswordRequest) =>
      api.post('auth/reset-password', { json: values }).json<MessageResponse>(),
    onSuccess: () => {
      toast.success('Password updated successfully! Please login.');
      navigate({ to: routes.auth.login });
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update password');
    },
  });
};

export const logoutMutation = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return mutationOptions({
    mutationFn: async () => {
      return await api.post('auth/logout');
    },
    onSuccess: () => {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');

      queryClient.resetQueries({ queryKey: queryKeys.user.current() });

      toast.success('Logged out successfully');

      navigate({ to: routes.auth.login });
    },
  });
};
